import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

import { AddressListLocation, AddressListParentLocationRef } from '../../../models/address-list-location';

import { GxInfoWindow } from '../../../utils/map/gx-infowindow';
import { GxMarker, GxMVCobject, INFO_WINDOW_DIMENSIONS, MapArtefact, MARKER_DIMENSIONS, MARKER_TOOLTIP_DIMENSIONS, MousePositionType } from '../../../utils/map/gx-map-utils';
import { encodeSVG, isMouseOverElement } from '../../../core/utilities';

export const MARKER_ID_DATA_ATTR = 'data-marker-id';
export const FOCUSED_ROW_CLASS_NAME = 'focusedRow';
export const MARKER_Z_INDICES = {
  parent: 1e5,
  // Let gmaps api decide -- we don't really care.
  // We only care about the parent being on the top.
  // More importantly, if we set this to 1, the markers *true* zIndices
  // will be shuffled on each `setOptions` call which will lead to blinking.
  child: undefined,
  highlight: 1e6,
};

@Injectable({
  providedIn: 'root',
})
export class BaseMapService {

  constructor() {
  }

  /**
   * Quick checker
   * @param address
   * @returns {boolean}
   */
  public static isParent(address: AddressListLocation) {
    return typeof address.parentLocation === 'undefined';
  }

  /**
   * Constructs a combination of $parentId_$placeId since
   * the same place IDs might occur multiple times throughout the AddList
   * and we need to uniquely identify the children.
   * @param {AddressListLocation | AddressListParentLocationRef} parentAddress
   * @param {AddressListLocation} childAddress
   */
  public static generateSafeChildId(
      parentAddress: AddressListLocation | AddressListParentLocationRef,
      childAddress: AddressListLocation,
  ) {
    return `${parentAddress?.id}_${childAddress?.placeId}`;
  }

  /**
   * Generate a marker w/ a custom svg.
   * @param {AddressListLocation} location
   * @param {string} id
   * @param {google.maps.Map} map
   * @param {AddressListLocation[]} filteredAddressListLocations
   * @param {GxMarker['meta']} metadata
   * @param {boolean} customPinsMinimized
   * @returns {GxMarker}
   */
  public generateMarker<Marker extends GxMarker>(
      location: AddressListLocation,
      id: string,
      map: google.maps.Map | google.maps.StreetViewPanorama,
      filteredAddressListLocations: AddressListLocation[],
      metadata?: Marker['meta'],
      customPinsMinimized?: boolean,
  ): Marker {
    const isParent = BaseMapService.isParent(location);

    let defaultIcon: GxMarker['meta']['icons']['defaultIcon'];
    let highlightedIcon: GxMarker['meta']['icons']['highlightedIcon'];
    let minimizedIcon: GxMarker['meta']['icons']['minimizedIcon'];

    if (metadata?.icons?.alreadyContainsSvgIcons) {
      defaultIcon = metadata.icons.defaultIcon;
      highlightedIcon = metadata.icons.highlightedIcon;
      minimizedIcon = metadata.icons.minimizedIcon;
    } else {
      ({
        defaultIcon,
        highlightedIcon,
        minimizedIcon,
      } = this.prepareMarkerIcons<Marker>(location, id, filteredAddressListLocations, metadata));
    }

    const marker = new google.maps.Marker({
      map: map,
      position: metadata?.positionOverride || this.extractPosition(location),
      icon: customPinsMinimized ? minimizedIcon : defaultIcon,
      zIndex: isParent ? MARKER_Z_INDICES.parent : MARKER_Z_INDICES.child,
    }) as Marker;

    // adding the icons like this and not in the constructor b/c it's difficult to extend
    // the default Marker constructor (and there's frankly no need to do so)
    marker['meta'] = {
      id: id,
      icons: {
        defaultIcon,
        highlightedIcon,
        minimizedIcon,
      },
      ...(metadata ? metadata : {}),
    };

    return marker;
  }

  /**
   * Extracts latitude and longitude of a location's validated coordinates.
   * (Validated coordinates always exist, even if the location is not validated yet. In this case,
   * the GBP coordinates serve as Validated coordinates (comes like that from the BE already). Only
   * in the DEV environment Validated coordinates are empty. In this case, we show
   * importedCoordinates instead so that the map shows some pins.)
   * @param location Coordinates of this location will be extracted
   * @param args
   */
  protected extractPosition(location: AddressListLocation, ...args: any): google.maps.LatLngLiteral {
    const coordinates = location.coordinate || location.coordinateImport;
    return {
      lat: coordinates?.latitude,
      lng: coordinates?.longitude,
    };
  }

  /**
   * Generate GxMarker icons
   * @param location
   * @param id
   * @param filteredAddressListLocations
   * @param metadata
   * @returns {GxMarker['meta']['icons']}
   * @private
   */
  public prepareMarkerIcons<Marker extends GxMarker>(
      location: AddressListLocation,
      id: string,
      filteredAddressListLocations: AddressListLocation[],
      metadata?: GxMarker['meta'],
  ): Marker['meta']['icons'] {
    const markerText = this.prepareMarkerText<Marker>(location, filteredAddressListLocations, metadata);
    const markerColor = this.prepareMarkerColor<Marker>(location, metadata);

    const rawSvgString = BaseMapService.prepareMarkerSvgScaffolding(
        location,
        markerText,
        markerColor,
        false,
        { ...metadata, id });
    const encodedSvgUri = encodeSVG(rawSvgString);

    const rawHighlightedSvgString = BaseMapService.prepareMarkerSvgScaffolding(
        location,
        markerText,
        markerColor,
        true,
        { ...metadata, id });
    const encodedHighlightedSvgUri = encodeSVG(rawHighlightedSvgString);

    const encodedMinimizedSvgString = encodeSVG(this.prepareMinimizedMarkerSvgScaffolding(markerColor));

    // used in claim-and-clean-menu.component to render the svg icon
    location.markerSvgUri = rawSvgString;

    const size = new google.maps.Size(
        MARKER_DIMENSIONS.w,
        MARKER_DIMENSIONS.h,
    );
    const minSize = new google.maps.Size(12, 12);
    const anchor = new google.maps.Point(MARKER_DIMENSIONS.w / 2, MARKER_DIMENSIONS.h);
    const minAnchor = new google.maps.Point(minSize.width / 2, minSize.height);

    return {
      defaultIcon: {
        url: encodedSvgUri,
        size,
        anchor,
      },
      highlightedIcon: {
        url: encodedHighlightedSvgUri,
        size,
        anchor,
      },
      minimizedIcon: {
        url: encodedMinimizedSvgString,
        size: minSize,
        anchor: minAnchor,
      },
    };
  }

  /**
   *
   * @param location
   * @param filteredAddressListLocations
   * @param metadata
   * @returns {string | undefined}
   * @private
   */
  protected prepareMarkerText<Marker extends GxMarker>(
      location: AddressListLocation,
      filteredAddressListLocations?: AddressListLocation[],
      metadata?: Marker['meta']): string | undefined {
    return undefined;
  }

  protected prepareMarkerColor<Marker extends GxMarker>(location: AddressListLocation, metadata?: Marker['meta']): string {
    return 'white';
  }

  /**
   * creates a 12x12 dot of the same color as the default marker icon
   * @param markerColor
   * @private
   */
  private prepareMinimizedMarkerSvgScaffolding(markerColor: string) {
    return `<svg xmlns='http://www.w3.org/2000/svg' height='12' viewBox='0 0 12 12' width='12' fill='${markerColor}'><path d='M12 12H0V0h12v12z' fill='none'/><circle cx='6' cy='6' r='4'/></svg>`;
  }

  /**
   * Returns an colored svg string populated with the `markerText`, if applicable.
   * @param {AddressListLocation} location
   * @param {string | undefined} markerText
   * @param {string} markerColor
   * @param {boolean | string} withHighlightFilter
   * @param {GxMarker['meta']} metadata
   * @private
   */
  private static prepareMarkerSvgScaffolding(
      location: AddressListLocation,
      markerText: string | undefined,
      markerColor: string,
      withHighlightFilter: boolean | string = false,
      metadata: GxMarker['meta'],
  ): string {
    // fall back to empty str if `false`
    if (!withHighlightFilter) {
      withHighlightFilter = '';
    }

    if (withHighlightFilter && withHighlightFilter === true) {
      withHighlightFilter = 'filter: brightness(90%)';
    }

    const isParent = BaseMapService.isParent(location);
    // Cast to a string to directly pass into the svg.
    // If we did not cast, `undefined` would've been converted to `"undefined"`.
    const markerTextAsString = markerText || '';

    const iconSvgs = {
      noContent: `
        <svg ${MARKER_ID_DATA_ATTR}="${metadata?.id}" xmlns='http://www.w3.org/2000/svg' width='40' height='56' viewBox='0 0 40 56'
             style='${withHighlightFilter}'>
          <g transform='translate(-150 -163.947)'>
            <path fill='#fff'
              d='M170,163.947a20,20,0,0,0-20,20c0,16,18,23,18,33v1a2,2,0,1,0,4,0v-1c0-10,18-17,18-33A20,20,0,0,0,170,163.947Z'/>
            <path
              fill='${markerColor}'
              d='M189,183.944c0,8.55-5.47,14.53-10.3,19.8-3.96,4.32-7.7,8.4-7.7,13.2a1,1,0,1,1-2,0c0-4.8-3.73-8.88-7.69-13.2-4.83-5.27-10.31-11.25-10.31-19.8a19,19,0,0,1,38,0Z'/>
            <circle fill='#fff' cx='8' cy='8' r='8' transform='translate(162 175.947)'/>
          </g>
        </svg>
      `,
      // a simplified copy of 'default_marker_icon.svg'
      gxMarkerDefault: `
        <svg ${MARKER_ID_DATA_ATTR}="${metadata?.id}" xmlns='http://www.w3.org/2000/svg' width='40' height='56' viewBox='0 0 40 56'>
          <path fill="#ffffff"
                d="M20,0A20,20,0,0,0,0,20C0,36,18,43,18,53v1a2,2,0,0,0,4,0V53c0-10,18-17,18-33A20,20,0,0,0,20,0Z" />
          <path fill="#00478f"
                d="M39,20c0,8.55-5.47,14.53-10.3,19.8-4,4.32-7.7,8.4-7.7,13.2a1,1,0,0,1-2,0c0-4.8-3.73-8.88-7.69-13.2C6.48,34.53,1,28.55,1,20a19,19,0,0,1,38,0Z" />
          <circle fill="#ffffff" r="8" cy="20.0" cx="20.0" />
        </svg>
      `,
      withTextualContent: `
        <svg ${MARKER_ID_DATA_ATTR}="${metadata?.id}" xmlns='http://www.w3.org/2000/svg' width='40' height='56' viewBox='0 0 40 56'
             style='${withHighlightFilter}'>
          <g transform='translate(-150 -163.947)'>
            <path fill='#fff'
              d='M170,163.947a20,20,0,0,0-20,20c0,16,18,23,18,33v1a2,2,0,1,0,4,0v-1c0-10,18-17,18-33A20,20,0,0,0,170,163.947Z'/>
            <path fill='${markerColor}'
              d='M189,183.944c0,8.55-5.47,14.53-10.3,19.8-3.96,4.32-7.7,8.4-7.7,13.2a1,1,0,1,1-2,0c0-4.8-3.73-8.88-7.69-13.2-4.83-5.27-10.31-11.25-10.31-19.8a19,19,0,0,1,38,0Z'/>
            <text fill='#fff'
                  style='font-size:16px;
                         font-family: Roobert, sans-serif;
                         font-weight:700'
                  text-anchor='middle'
                  transform='translate(170 190.947)'>
              <tspan y='0'>${markerTextAsString}</tspan>
            </text>
          </g>
        </svg>
      `,
      // TODO TBD Start thinking about clustering in the future. I'm keeping this here for easy access.
      withClusterEllipsis: `
        <svg ${MARKER_ID_DATA_ATTR}="${metadata?.id}" xmlns='http://www.w3.org/2000/svg' width='40' height='56' viewBox='0 0 40 56'
             style='${withHighlightFilter}'>
          <g transform='translate(-150 -163.947)'>
            <path fill='#FFF'
              d='M170,163.947a20,20,0,0,0-20,20c0,16,18,23,18,33v1a2,2,0,1,0,4,0v-1c0-10,18-17,18-33A20,20,0,0,0,170,163.947Z'/>
            <path fill='${markerColor}'
              d='M189,183.944c0,8.55-5.47,14.53-10.3,19.8-3.96,4.32-7.7,8.4-7.7,13.2a1,1,0,1,1-2,0c0-4.8-3.73-8.88-7.69-13.2-4.83-5.27-10.31-11.25-10.31-19.8a19,19,0,0,1,38,0Z'/>
            <path fill='#FFF'
              d='M3245-17939a2,2,0,0,1,2-2,2,2,0,0,1,2,2,2,2,0,0,1-2,2A2,2,0,0,1,3245-17939Zm-8,0a2,2,0,0,1,2-2,2,2,0,0,1,2,2,2,2,0,0,1-2,2A2,2,0,0,1,3237-17939Zm-8,0a2,2,0,0,1,2-2,2,2,0,0,1,2,2,2,2,0,0,1-2,2A2,2,0,0,1,3229-17939Z'
              transform='translate(-3069 18122.947)'/>
          </g>
        </svg>
      `,
    };

    if (metadata?.icons?.defaultToInternalGxMarkerSvg) {
      return iconSvgs.gxMarkerDefault;
    }

    if (isParent) {
      return markerText ? iconSvgs.withTextualContent : iconSvgs.noContent;
    } else {
      return iconSvgs.withTextualContent;
    }
  }

  /**
   * Generates a new instance of the `GxInfoWindow`
   * @param type
   * @param position
   * @param map
   * @param opts
   */
  public generateGxInfoWindow(
      type: GxMVCobject,
      position: google.maps.LatLngLiteral,
      map: google.maps.Map,
      opts?: {
        content?: string,
        optOptionsOverride?: Parameters<typeof GxInfoWindow>[0]
      } & {
        // let any number of key-value pairs thru (esp. in the method's overrides)
        [key: string]: any
      },
  ) {
    return new GxInfoWindow({
      map: map,
      content: opts?.content || '',

      position: position,
      pixelOffset:
          type === GxMVCobject.INFO_WINDOW
              ? new google.maps.Size(
                  // 1/2 of the info window to the left to center it
                  (INFO_WINDOW_DIMENSIONS.w / 2) + 5,
                  // -5px (=+5px to the top) to let the marker breathe a little
                  -MARKER_DIMENSIONS.h - 5,
              )
              : new google.maps.Size(
                  // 1/2 of the info window to the left to center it
                  (MARKER_TOOLTIP_DIMENSIONS.w / 2),
                  // 1/2 of the tooltip height down the y axis + 25px so that tooltip does not overlap
                  // marker and the mouseout event gets registered properly
                  (MARKER_TOOLTIP_DIMENSIONS.h / 2) + 25,
              ),

      minWidth: type === GxMVCobject.INFO_WINDOW
          ? (INFO_WINDOW_DIMENSIONS.w - INFO_WINDOW_DIMENSIONS.padding * 2)
          : (MARKER_TOOLTIP_DIMENSIONS.w - MARKER_TOOLTIP_DIMENSIONS.padding * 2),
      minHeight: type === GxMVCobject.INFO_WINDOW
          ? (INFO_WINDOW_DIMENSIONS.h - INFO_WINDOW_DIMENSIONS.padding * 2)
          : (MARKER_TOOLTIP_DIMENSIONS.h - MARKER_TOOLTIP_DIMENSIONS.padding * 2),
      maxHeight: type === GxMVCobject.INFO_WINDOW
          ? (INFO_WINDOW_DIMENSIONS.h - INFO_WINDOW_DIMENSIONS.padding * 2)
          : (MARKER_TOOLTIP_DIMENSIONS.h - MARKER_TOOLTIP_DIMENSIONS.padding * 2),

      padding: type === GxMVCobject.INFO_WINDOW
          ? INFO_WINDOW_DIMENSIONS.padding
          : MARKER_TOOLTIP_DIMENSIONS.padding,

      backgroundColor: type === GxMVCobject.INFO_WINDOW ? 'white' : 'rgba(37, 38, 41, 0.72)',
      color: type === GxMVCobject.INFO_WINDOW ? '#252629' : 'white',
      borderRadius: type === GxMVCobject.INFO_WINDOW ? 8 : 4,

      borderWidth: 0,
      borderColor: 'transparent',

      // don't move the map around when we're just hovering
      disableAutoPan: type === GxMVCobject.TOOLTIP,

      // allow full overrides
      ...(opts.optOptionsOverride ? opts.optOptionsOverride : {}),
    });
  }

  /**
   * Binds the marker click to open up the popup and returns an array of gmaps listeners
   * @param {GxMarker} marker
   * @param {google.maps.LatLngLiteral} position
   * @param {google.maps.Map} map
   * @param opts
   * @private
   */
  bindActionsPopup(
      marker: GxMarker,
      position: google.maps.LatLngLiteral,
      map: google.maps.Map,
      opts?: {
        onContentClick?: Function
      },
  ): google.maps.MapsEventListener[] {

    return [
      // open the popup upon marker click
      google.maps.event.addListener(marker, 'click', () => {
        marker?.meta['actionsPopupInstance'].open();

        if (opts?.onContentClick) {
          const node = (marker?.meta['actionsPopupInstance'].content_ as HTMLDivElement);
          // `addDomListenerOnce` is deprecated as per https://developers.google.com/maps/deprecations?hl=en#googlemapseventadddomlistener_and_googlemapseventadddomlisteneronce_deprecated_on_april_7_2022
          // so we'll need to use `addEventListener`
          // w/ `once` as an option: https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/addEventListener#parameters
          node?.addEventListener('click', () => {
            opts?.onContentClick();
          }, { once: true });
        }
      }),
      // close the popup upon map click
      google.maps.event.addListener(map, 'click', () => {
        marker?.meta['actionsPopupInstance'].close();
      }),
    ];
  }

  /**
   * Binds double-clicking a marker to scroll the respective row into view
   * @param {GxMarker} marker
   * @param {string} rawRowSelector
   * @param {Subject} onMarkerDblClick$
   */
  bindBringMarkerRowIntoView(
      marker: GxMarker,
      rawRowSelector: string,
      onMarkerDblClick$: Subject<Element>): google.maps.MapsEventListener[] {
    const rawMatRowInQuestion = document.querySelector(rawRowSelector);

    if (!rawMatRowInQuestion) {
      BaseMapService._logSomethingWentWrong('bindBringMarkerRowIntoView', {
        location,
        rawRowSelector,
      }, 'No mat-row to work with. Bailing out.');

      return;
    }

    return [
      // bring this row into view
      google.maps.event.addListener(marker, 'dblclick', () => {
        onMarkerDblClick$.next(rawMatRowInQuestion);
      }),
    ];
  }

  /**
   * Binds hovering over a marker to open up the tooltip and returns an array of gmaps listeners
   * @param {GxMarker} marker
   * @param {AddressListLocation} location
   * @param {BehaviorSubject} debouncedOnMarkerMouseover$
   */
  public bindMarkerTooltip(
      marker: GxMarker,
      location: AddressListLocation,
      debouncedOnMarkerMouseover$: BehaviorSubject<{
        location: AddressListLocation,
        marker: GxMarker
      }>): google.maps.MapsEventListener[] {
    return [
      // open the popup upon marker mouseover
      google.maps.event.addListener(marker, 'mouseover', () => {
        debouncedOnMarkerMouseover$.next({ location, marker });
      }),
    ];
  }

  /**
   * Bind mat table rows mouseover & mouseout events and delegates them to the respective marker
   * @param {GxMarker} marker
   * @param {string} rawRowSelector
   * @param {BehaviorSubject<MousePositionType>} mousePosition$
   */
  public bindRowHover(
      marker: GxMarker,
      rawRowSelector: string,
      mousePosition$: BehaviorSubject<MousePositionType>) {
    const rawMatRowInQuestion = document.querySelector(rawRowSelector);

    if (!rawMatRowInQuestion) {
      BaseMapService._logSomethingWentWrong('bindRowHover', {
        location,
        rawRowSelector,
      }, 'No mat-row to work with. Bailing out.');

      return;
    }

    // Previously, we were using `addDomEventListener` but since it's now deprecated
    // (Apr 2022 - https://developers.google.com/maps/deprecations?hl=en#googlemapseventadddomlistener_and_googlemapseventadddomlisteneronce_deprecated_on_april_7_2022),
    // so we'll register the `mouseover` and `mouseout` events separately and return an empty array.

    rawMatRowInQuestion.addEventListener('mouseover', (_ => {
      const mousePosition = mousePosition$.getValue();

      const isCurrentlyHovered = isMouseOverElement(
          mousePosition,
          rawMatRowInQuestion,
      );
      // don't delegate down to the marker `mouseover` handler because
      // it already is "officially" hovered when it has its `FOCUSED_ROW_CLASS_NAME`
      // which could've only been added thru the marker's `mouseover` handler.
      // If we repeatedly let this thru (since each DOM mouse move triggers a `mouseover`),
      // it'll lead to blinking and unnecessary event bubbling.
      if (
          isCurrentlyHovered
          && rawMatRowInQuestion?.classList.contains(FOCUSED_ROW_CLASS_NAME)
      ) return;

      google.maps.event.trigger(marker, 'mouseover');
    }));

    rawMatRowInQuestion.addEventListener('mouseout', (_ => {
      // the `mouseout` event tends to fire way too fast and
      // the `mousePosition` doesn't keep up and acts as if the
      // row is still being hovered so let's defer the mouse-position check
      // by a few millis.
      setTimeout(() => {
        const mousePosition = mousePosition$.getValue();

        const isCurrentlyHovered = isMouseOverElement(
            mousePosition,
            rawMatRowInQuestion,
        );

        // we're still hovering -- don't delegate just yet
        if (isCurrentlyHovered) return;

        google.maps.event.trigger(marker, 'mouseout');
      }, 60);
    }));

    // The reason for returning an empty array is that the native DOM `addEventListener` returns
    // `void` so there's nothing to "store" and later de-register using `removeEventListener`,
    // like with standard, non-DOM google maps events.
    // As such, we'll let the browser garbage collector take care of detaching the `mouseover` and
    // `mouseout` events.
    return [];
  }

  /**
   * Center and zoom into a marker's position on the map
   * upon clicking the mat row marker
   * @param {GxMarker} marker
   * @param {string} rawRowSelector
   * @param {google.maps.Map} map
   */
  public bindRowMarkerClick(marker: GxMarker, rawRowSelector: string, map: google.maps.Map) {
    const rawRowMarkerBtn = document.querySelector(`${rawRowSelector} .map-pin-action`);

    if (!rawRowMarkerBtn) {
      BaseMapService._logSomethingWentWrong('bindRowHover', {
        rawRowSelector,
        marker,
      }, 'No mat-row svg to work with. Bailing out.');

      return;
    }

    rawRowMarkerBtn.addEventListener('click', _ => {
      map.setCenter(marker.getPosition());
      map.setZoom(map.get('maxZoom') || 18);
    });

    // Same principle as in `bindRowHover` above.
    return [];
  }

  /**
   * When custom pins in CV/Diff map are minimized, bind hover events to make pins great again on
   * hover
   * @param mapArtefact
   */
  public static bindTogglePinSizeHover<Marker extends GxMarker>(mapArtefact: MapArtefact<Marker>) {
    if (!mapArtefact.parentLocation.togglePinSizeEventListeners) {
      mapArtefact.parentLocation.togglePinSizeEventListeners = [];
    }

    mapArtefact.parentLocation.togglePinSizeEventListeners.push(google.maps.event.addListener(
        mapArtefact.parentLocation.marker,
        'mouseover', () => {
          mapArtefact.parentLocation.marker.setIcon(mapArtefact.parentLocation.marker.meta.icons.defaultIcon);
        }));
    mapArtefact.parentLocation.togglePinSizeEventListeners.push(google.maps.event.addListener(
        mapArtefact.parentLocation.marker,
        'mouseout', () => {
          mapArtefact.parentLocation.marker.setIcon(mapArtefact.parentLocation.marker.meta.icons.minimizedIcon);
        }));
  }

  /**
   * ARTEFACT MANIPULATION
   */

  /**
   * Updates an artefact based on its parent & child index.
   * The `newArtefactParams` can be a subset of the full params a given artefact contains.
   * @param indices
   * @param newArtefactParams
   * @param mapArtefacts
   */
  public updateArtefact(
      indices: Partial<ReturnType<BaseMapService['findArtefactGroup']>>,
      newArtefactParams: Partial<MapArtefact['parentLocation']
          | MapArtefact['childrenLocations'][number]>,
      mapArtefacts: MapArtefact[],
  ): void {
    const { parentIndex, childIndex } = indices;

    // we're dealing w/ a parent
    if (typeof childIndex === 'undefined' || childIndex === -1) {
      mapArtefacts[parentIndex].parentLocation = {
        ...mapArtefacts[parentIndex].parentLocation,
        ...newArtefactParams,
      };
      return;
    }

    mapArtefacts[parentIndex].childrenLocations[childIndex] = {
      ...mapArtefacts[parentIndex].childrenLocations[childIndex],
      ...newArtefactParams,
    };
  }

  /**
   * Toggles custom pin's size and adds hover listeners
   * @param mapArtefacts
   * @param customPinsMinimized
   */
  public static togglePinSize<Marker extends GxMarker>(mapArtefacts: MapArtefact<Marker>[], customPinsMinimized: boolean) {
    mapArtefacts.forEach(artefact => {
      if (customPinsMinimized) {
        artefact.parentLocation.marker.setIcon(artefact.parentLocation.marker.meta.icons.minimizedIcon);
        BaseMapService.bindTogglePinSizeHover(artefact);
      } else {
        artefact.parentLocation.marker.setIcon(artefact.parentLocation.marker.meta.icons.defaultIcon);
        BaseMapService.flushTogglePinSizeEventListeners(artefact);
      }
    });
  }

  /*
   * CLEANUP
   */

  /**
   * Flush parent artefact (parent in the CC sense but also any other non-CC row).
   * @param artefactGroup
   * @param overlappingMarkerSpiderfier
   */
  public static flushParentArtefact<Marker extends GxMarker>(artefactGroup: MapArtefact<Marker>, overlappingMarkerSpiderfier) {
    overlappingMarkerSpiderfier?.forgetMarker(artefactGroup.parentLocation.marker);
    artefactGroup.parentLocation.marker?.setMap(null);
    artefactGroup.parentLocation.radiusCircle?.setMap(null);
    artefactGroup.parentLocation.actionsPopup?.setMap(null);
    artefactGroup.parentLocation.hoverTooltip?.setMap(null);
    artefactGroup.parentLocation.eventListeners?.map(listener => google.maps.event.removeListener(listener));
    artefactGroup.parentLocation.togglePinSizeEventListeners?.map(listener => google.maps.event.removeListener(listener));
  }

  /**
   * Flush children artefacts (CC only).
   * @param artefactGroup
   * @param overlappingMarkerSpiderfier
   */
  public static flushChildrenArtefacts<Marker extends GxMarker>(artefactGroup: MapArtefact<Marker>, overlappingMarkerSpiderfier) {
    artefactGroup.childrenLocations?.map(childLocation => {
      overlappingMarkerSpiderfier?.forgetMarker(childLocation.marker);
      childLocation.marker?.setMap(null);
      childLocation.actionsPopup?.setMap(null);
      childLocation.hoverTooltip?.setMap(null);
      childLocation.eventListeners?.map(listener => google.maps.event.removeListener(listener));
      artefactGroup.parentLocation.togglePinSizeEventListeners?.map(listener => google.maps.event.removeListener(listener));
    });
  }

  /**
   * Destroys all map bindings like markers, spiderfiers, listeners etc.
   */
  public static flushMapArtefacts<Marker extends GxMarker>(mapArtefacts: MapArtefact<Marker>[], overlappingMarkerSpiderfier) {
    if (!mapArtefacts.length) {
      return [];
    }

    mapArtefacts.map(group => {
      BaseMapService.flushParentArtefact(group, overlappingMarkerSpiderfier);
      BaseMapService.flushChildrenArtefacts(group, overlappingMarkerSpiderfier);
    });

    mapArtefacts = [];
    return mapArtefacts;
  }

  /**
   * Destroy toggle custom pins' size on hover event listeners
   * @param mapArtefact
   */
  public static flushTogglePinSizeEventListeners<Marker extends GxMarker>(mapArtefact: MapArtefact<Marker>) {
    mapArtefact.parentLocation.togglePinSizeEventListeners?.map(listener => listener.remove());
  }

  /**
   * LOOKUPS
   */

  /**
   * Attempts to find the artefact group. Also returns the helper indices for easier updates.
   * @param location
   * @param mapArtefacts
   * @private
   */
  public findArtefactGroup(location: AddressListLocation, mapArtefacts: MapArtefact[]): {
    topLevelArtefact: MapArtefact;
    parentIndex: number;
    childIndex: number;
  } {
    const isParent = BaseMapService.isParent(location);

    if (isParent) {
      const parentIndex = mapArtefacts.findIndex(group => group.parentLocation.id === location.id.toString());

      // nothing to do here
      if (parentIndex === -1) {
        return {
          topLevelArtefact: undefined,
          parentIndex: -1,
          childIndex: -1,
        };
      }

      return {
        topLevelArtefact: mapArtefacts[parentIndex],
        parentIndex: parentIndex,
        childIndex: -1,
      };
    }

    const parentIndex = mapArtefacts.findIndex(
        group => group.parentLocation.id === location.parentLocation.id.toString(),
    );

    // nothing to do here
    if (parentIndex === -1) {
      return {
        topLevelArtefact: undefined,
        parentIndex: -1,
        childIndex: -1,
      };
    }

    // locate the child based on its `safeId`
    const childIndex = mapArtefacts[parentIndex].childrenLocations.findIndex(
        child => child.safeId === BaseMapService
            .generateSafeChildId(location.parentLocation, location),
    );

    return {
      topLevelArtefact: mapArtefacts[parentIndex],
      parentIndex: parentIndex,
      childIndex: childIndex,
    };
  }

  /**
   * Easily flatten all artefacts of a given type
   * @param type
   * @param opts
   * @param mapArtefacts
   */
  public findAllArtefacts(type: GxMVCobject, opts: {
    childrenOnly?: boolean,
    parentsOnly?: boolean,
    parentId?: MapArtefact['parentLocation']['id'],
  } = {
    childrenOnly: false,
    parentsOnly: false,
    parentId: undefined,
  }, mapArtefacts: MapArtefact[]): any[] {
    return mapArtefacts
        .filter(artefact => opts.parentId
            // do check if applicable
            ? artefact.parentLocation.id === opts.parentId
            // fallback -- let all artefacts thru
            : true)
        .reduce((acc, topLevelArtefact) => {
          return acc.concat(
              [].concat(
                  opts.childrenOnly
                      ? [] // skip the parent
                      : (
                          // include the parent
                          (() => {
                            switch (type) {
                              case GxMVCobject.INFO_WINDOW:
                                return topLevelArtefact.parentLocation.actionsPopup;
                              case GxMVCobject.TOOLTIP:
                                return topLevelArtefact.parentLocation.hoverTooltip;
                              case GxMVCobject.MARKER:
                                return topLevelArtefact.parentLocation.marker;
                              default:
                                return [];
                            }
                          }))(),
                  // using an IIFE to be able to use switch case
                  // https://developer.mozilla.org/en-US/docs/Glossary/IIFE

              ),
          ).concat(
              [].concat(
                  opts.parentsOnly
                      ? [] // skip the children
                      : (
                          // include the children
                          (() => {
                            switch (type) {
                              case GxMVCobject.INFO_WINDOW:
                                return topLevelArtefact.childrenLocations.map(child => child.actionsPopup);
                              case GxMVCobject.TOOLTIP:
                                return topLevelArtefact.childrenLocations.map(child => child.hoverTooltip);
                              case GxMVCobject.MARKER:
                                return topLevelArtefact.childrenLocations.map(child => child.marker);
                              default:
                                return []; // fallback
                            }
                          }))(),
                  // using an IIFE to be able to use switch case
                  // https://developer.mozilla.org/en-US/docs/Glossary/IIFE
              ),
          );
        }, []);
  }

  /**
   * HELPERS
   */

  /**
   * Gracefully & asynchronously set an MVC object's icon URL.
   * @param opts
   */
  deferSetMVCIcon(opts: {
    mvcObject: google.maps.Marker,
    // url to test against
    imgUrl: string | null,
    // fallback
    defaultIcon: string | google.maps.Icon | google.maps.Symbol,
    onSuccessCallback?: Function,
    onErrorCallback?: Function
  } = {
    mvcObject: null,
    imgUrl: null,
    defaultIcon: '',
    onSuccessCallback: () => {
    },
    onErrorCallback: () => {
    },
  }) {
    const tmp_img = new Image();

    tmp_img.onload = () => {
      opts.mvcObject.setIcon(opts.imgUrl);

      if (opts.onSuccessCallback) opts.onSuccessCallback();
    };

    tmp_img.onerror = () => {
      opts.mvcObject.setIcon(opts.defaultIcon);

      if (opts.onErrorCallback) opts.onErrorCallback();
    };

    if (opts.imgUrl) {
      tmp_img.src = opts.imgUrl;
    }

    // no need to remove the tmp_img b/c
    // the garbage collecor will handle it
    // https://stackoverflow.com/a/20495345/8160318
  }

  /**
   * Wrapper around `isParent`.
   * @param location
   */
  static extractParentIdString(location: AddressListLocation): string {
    const isParent = BaseMapService.isParent(location);
    return isParent ? `${location.id}` : `${location.parentLocation.id}`;
  }

  /**
   * Constructs a selector string to be used in `document.querySelector`
   * @param parentLocation
   * @param childLocation
   * @private
   */
  static constructRowSelectorString(parentLocation: AddressListLocation, childLocation: AddressListLocation | undefined = undefined) {
    return !childLocation
        ? `mat-row[data-row-parent-id="${parentLocation?.id}"]`
        : `mat-row[data-row-safe-id="${
            BaseMapService.generateSafeChildId(
                parentLocation,
                childLocation)
        }"]`;
  }

  /**
   * Logging helper
   * @param scope
   * @param args
   * @param msg
   */
  public static _logSomethingWentWrong(
      scope: string,
      args = {},
      msg = 'No artefact to work with...',
  ) {
    console.warn(`[${scope}] ${msg}`, { ...args });
  }
}
